import React, { createContext, useContext, useEffect } from 'react';
import { Outlet, NavLink, useParams, useLocation } from 'react-router-dom';
import { usePortal } from '@/hooks/usePortal';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard, TrendingUp, BarChart3, FileText, FolderOpen,
  Zap, Shield, Phone, Mail, CheckCircle2, Loader2, AlertCircle
} from 'lucide-react';

const PortalContext = createContext(null);
export const usePortalContext = () => useContext(PortalContext);

const TABS = [
  { to: '', label: 'Uebersicht', icon: LayoutDashboard, end: true },
  { to: 'markt', label: 'Marktdaten', icon: TrendingUp },
  { to: 'verbrauch', label: 'Verbrauch', icon: BarChart3 },
  { to: 'angebote', label: 'Angebote', icon: FileText },
  { to: 'dokumente', label: 'Dokumente', icon: FolderOpen },
];

const CustomerPortalLayout = () => {
  const { token } = useParams();
  const location = useLocation();
  const portal = usePortal(token);

  // Track page view on every navigation
  useEffect(() => {
    if (portal.data) {
      portal.trackEvent('portal_page_view', { path: location.pathname });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, portal.data]);

  if (portal.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="text-primary animate-spin" size={32} />
          <p className="text-sm text-muted-foreground">Portal wird geladen...</p>
        </div>
      </div>
    );
  }

  if (portal.error || !portal.data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="text-destructive" size={32} />
          </div>
          <h2 className="text-xl font-bold text-foreground">Portal nicht gefunden</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Der Link ist moeglicherweise ungueltig oder das Portal wurde deaktiviert.
            Bitte kontaktieren Sie uns falls Sie Hilfe brauchen.
          </p>
          <a href="tel:+4917012345678" className="inline-flex items-center gap-2 mt-6 bg-primary text-primary-foreground px-5 py-2.5 rounded-lg font-semibold text-sm">
            <Phone size={16} />
            +49 170 123 456 78
          </a>
        </div>
      </div>
    );
  }

  const { data } = portal;
  const greeting = data.contact?.last_name ? `Herr ${data.contact.last_name}` : data.contact?.first_name || 'Kunde';
  const initials = (data.company?.name || 'AQ').split(' ').map(n => n[0]).join('').slice(0, 2);

  return (
    <PortalContext.Provider value={portal}>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            {/* Top row: brand + account info */}
            <div className="h-16 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Zap className="text-primary-foreground" size={20} strokeWidth={2.5} />
                </div>
                <div>
                  <div className="font-bold text-sm text-foreground leading-none">AIQCON Energy</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">Kundenportal</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="hidden md:flex items-center gap-2 text-xs text-muted-foreground">
                  <Shield size={12} className="text-success" />
                  <span>Verschluesselte Verbindung</span>
                </div>
                <div className="hidden sm:block text-right">
                  <div className="text-xs font-semibold text-foreground">{data.company?.name}</div>
                  <div className="text-[11px] text-muted-foreground">{greeting}</div>
                </div>
                <div className="hk-avatar size-md" style={{ background: 'var(--primary)' }}>{initials}</div>
              </div>
            </div>

            {/* Tab Navigation */}
            <nav className="flex items-center gap-1 overflow-x-auto -mb-px pb-0 scrollbar-thin">
              {TABS.map(tab => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.end}
                  className={({ isActive }) => cn(
                    'flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors',
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  )}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </header>

        {/* Content */}
        <main className="max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:py-8">
          <Outlet />
        </main>

        {/* Footer - Trust Signals */}
        <footer className="border-t border-border bg-card mt-12">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <Zap className="text-primary-foreground" size={14} strokeWidth={2.5} />
                  </div>
                  <span className="font-bold text-sm">AIQCON Energy</span>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Professionelle Energiebeschaffung fuer Gewerbe und Industrie. Ueber 500 Unternehmen vertrauen uns.
                </p>
              </div>

              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Ihr Ansprechpartner</h4>
                <div className="flex items-center gap-3">
                  <div className="hk-avatar size-md" style={{ background: 'var(--primary)' }}>DA</div>
                  <div>
                    <div className="text-sm font-semibold text-foreground">{data.sales.full_name}</div>
                    <div className="flex items-center gap-3 mt-1">
                      <a href={`tel:${data.sales.phone}`} className="flex items-center gap-1 text-xs text-primary hover:underline">
                        <Phone size={11} />
                        {data.sales.phone}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Sicherheit &amp; Datenschutz</h4>
                <div className="space-y-2 text-xs text-muted-foreground">
                  <div className="flex items-center gap-2"><CheckCircle2 size={12} className="text-success" />DSGVO-konform, Datenhaltung in Deutschland</div>
                  <div className="flex items-center gap-2"><CheckCircle2 size={12} className="text-success" />TLS-verschluesselte Uebertragung</div>
                  <div className="flex items-center gap-2"><CheckCircle2 size={12} className="text-success" />Keine Weitergabe an Dritte</div>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
              <div>&copy; {new Date().getFullYear()} AIQCON GmbH &middot; Energieberatung &amp; Optimierung</div>
              <div className="flex items-center gap-4">
                <a href="#" className="hover:text-foreground">Impressum</a>
                <a href="#" className="hover:text-foreground">Datenschutz</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </PortalContext.Provider>
  );
};

export default CustomerPortalLayout;
